/**
 * The browser's SQLite binding — the page half.
 *
 * Specification: ../../../../apps/pos/test/opfs-driver.spec.ts.
 * Master plan §3 K4, §3 K8.
 *
 * IT LIVES HERE RATHER THAN IN THE TILL because the kitchen screen needs it
 * too. A cook bumped a ticket during an outage, the screen was reloaded, and
 * the bump was gone — it had been queued in memory. The ticket came back onto
 * the pass when the network returned and the kitchen cooked it twice. The
 * outbox store beside this has always been durable; only the browser binding
 * was in one app's source tree.
 *
 * The POS ran `MemoryOutboxStore`, so a reload lost every queued sale and, once
 * shifts existed, the open drawer with them. `SqliteOutboxStore` has been
 * written and tested against real durability the whole time; only the browser
 * binding was missing.
 *
 * WHY THIS IS A MESSAGE PROTOCOL AND NOT A WRAPPER.
 *
 * Both of SQLite's OPFS VFSes must run in a Worker — the storage API they build
 * on is only available there. So the database lives in `./db-worker.ts` and
 * this posts to it. `SqliteDriver` was defined async for exactly this reason,
 * which is what lets the hub and the tests run the same store over bun:sqlite
 * with no second implementation.
 *
 * CORRELATION IDS RATHER THAN A QUEUE. Replies from an async database arrive
 * out of order as a matter of course, and a driver that assumed FIFO would hand
 * the second caller the first one's rows — silently, and only under load.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";

/** The slice of `Worker` this uses. Narrow so a double is a few lines. */
export interface DbWorkerPort {
  postMessage(message: unknown): void;
  set onmessage(handler: ((event: { data: unknown }) => void) | null);
  terminate(): void;
}

interface Reply {
  readonly id: number;
  readonly ok: boolean;
  readonly error?: string;
  readonly rows?: unknown[];
}

/** Reserved for the worker's own "I am open for business" message. */
const READY_ID = 0;

export class OpfsSqliteDriver implements SqliteDriver {
  readonly #worker: DbWorkerPort;
  readonly #pending = new Map<number, { resolve: (rows: unknown[]) => void; reject: (e: Error) => void }>();

  #nextId = 1;
  #ready: Promise<void>;
  #markReady!: () => void;
  #failReady!: (error: Error) => void;
  #closed = false;

  constructor(worker: DbWorkerPort) {
    this.#worker = worker;
    this.#ready = new Promise<void>((resolve, reject) => {
      this.#markReady = resolve;
      this.#failReady = reject;
    });
    // Nothing rejects this promise if the worker simply never speaks. That is
    // deliberate rather than overlooked: a page whose worker died gets a
    // permanently pending write, and the layer that must notice is the one
    // that can do something about it. Adding a timeout here would convert a
    // visible hang into a dropped sale.
    this.#ready.catch(() => {});

    worker.onmessage = (event) => this.#receive(event.data as Reply);
  }

  #receive(reply: Reply): void {
    if (reply.id === READY_ID) {
      if (reply.ok) this.#markReady();
      else this.#failReady(new Error(reply.error ?? "the database worker failed to start"));
      return;
    }

    const waiting = this.#pending.get(reply.id);
    // An unknown id is ignored. A late reply after a teardown, or a message
    // from something else sharing the port — throwing here would take down the
    // till's event loop for a message that means nothing.
    if (!waiting) return;
    this.#pending.delete(reply.id);

    if (reply.ok) waiting.resolve(reply.rows ?? []);
    else waiting.reject(new Error(reply.error ?? "the database rejected the statement"));
  }

  async #send(op: "exec" | "run" | "all", sql: string, params: readonly unknown[]): Promise<unknown[]> {
    // AWAITED FIRST. The worker has a wasm binary to fetch, a VFS to install
    // and a database to open; a statement posted before that lands against a
    // database that does not exist, and the failure is a sale never queued.
    await this.#ready;
    if (this.#closed) throw new Error("the database is closed");

    const id = this.#nextId++;
    return new Promise<unknown[]>((resolve, reject) => {
      this.#pending.set(id, { resolve, reject });
      this.#worker.postMessage({ id, op, sql, params });
    });
  }

  async exec(sql: string): Promise<void> {
    await this.#send("exec", sql, []);
  }

  async run(sql: string, params: readonly unknown[]): Promise<void> {
    await this.#send("run", sql, params);
  }

  async all<T>(sql: string, params: readonly unknown[]): Promise<T[]> {
    return (await this.#send("all", sql, params)) as T[];
  }

  /**
   * Tear down, rejecting anything still in flight.
   *
   * A page being closed mid-drain. The awaiting code has to learn the write did
   * not happen, or `Outbox` records an attempt that never occurred and burns an
   * event's budget towards quarantine for a failure that was ours.
   */
  close(): void {
    this.#closed = true;
    for (const waiting of this.#pending.values()) {
      waiting.reject(new Error("the database closed while this statement was in flight"));
    }
    this.#pending.clear();
    this.#worker.terminate();
  }
}

/**
 * Build the driver over a real Worker.
 *
 * Separate from the class so the class is testable without one. `type: "module"`
 * because the worker imports the sqlite module, and the URL form is what lets
 * the bundler emit the worker as its own chunk with the wasm beside it.
 */
export function createOpfsDriver(): OpfsSqliteDriver {
  return new OpfsSqliteDriver(
    new Worker(new URL("./db-worker.ts", import.meta.url), { type: "module" }) as DbWorkerPort,
  );
}
